import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { entityService } from '../../../services/entityService';
import { createFileLogger } from '../../../utils/logger';
import { getNotesAppStore } from '../store/notesAppStore';

const log = createFileLogger('NOTES_COMMANDS');

export const notesCreateCommand: SelfContainedCommand = {
  id: 'notes_create',
  description: '📝 CREATE: Create a new note in NotesMarkdownApp',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Title for the new note',
        required: false
      },
      {
        name: 'createWindow',
        type: 'boolean',
        description: 'Whether to create a window for the note',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { title: 'Meeting Notes' },
        expectedOutput: {
          status: 'success',
          message: 'Created note: Meeting Notes',
          format: 'json',
          data: { noteId: 'note_123', title: 'Meeting Notes' }
        },
        description: 'Create a note with a specific title'
      }
    ],
    bad: []
  },
  
  handler: async (params: { title?: string; createWindow?: boolean }, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      log.info('[notes_create] Creating note...', { title: params.title });
      
      const now = new Date().toISOString();
      const noteTitle = params.title || 'Untitled Note';
      
      const note = await entityService.create({
        entityType: 'Note',
        name: noteTitle,
        skeleton: {
          '@type': 'Note',
          preview: '',
          createdAt: now,
          updatedAt: now
        }
      });
      
      if (params.createWindow !== false && context.spaceId) {
        const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
        const { executeCommand } = useCommandRegistryStore.getState();
        
        const result = (await executeCommand('space_open_window', {
          entityId: note._id || note.id
        })) as import('../../../types/standardToolResults').StandardToolResult;
        
        if (result.status !== 'success') {
          log.error('[notes_create] Failed to open window:', result.message || 'Unknown error');
        }
      }
      
      log.info('[notes_create] Note created:', { id: note.id });
      
      return createSuccessResult(
        `Created note: ${noteTitle}`,
        {
          noteId: note.id,
          title: noteTitle
        }
      );
    } catch (error) {
      log.error('[notes_create] Failed to create note:', error);
      return createErrorResult(
        'Failed to create note',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  }
};

export const notesOpenCommand: SelfContainedCommand = {
  id: 'notes_open',
  description: '📂 OPEN: Open a specific note by ID',
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'noteId',
        type: 'string',
        description: 'ID of the note to open',
        required: true
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Window ID to open the note in',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { noteId: 'note_abc123' },
        expectedOutput: {
          status: 'success',
          message: 'Opened note: My Note',
          format: 'json',
          data: { noteId: 'note_abc123', title: 'My Note' }
        },
        description: 'Open a note in a new window'
      }
    ],
    bad: []
  },
  
  handler: async (params: { noteId: string; windowId?: string }, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      log.info('[notes_open] Opening note...', { noteId: params.noteId });
      
      const note = await entityService.get(params.noteId);
      if (!note || note.entityType !== 'Note') {
        return createErrorResult(
          'Note not found',
          'The requested note does not exist',
          'validation'
        );
      }
      
      if (params.windowId && context.spaceId) {
        const store = getNotesAppStore(params.windowId, context.spaceId);
        store.getState().selectNote(params.noteId);
      } else if (context.spaceId) {
        const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
        const { executeCommand } = useCommandRegistryStore.getState();
        
        const result = (await executeCommand('space_open_window', {
          entityId: note._id || note.id
        })) as import('../../../types/standardToolResults').StandardToolResult;
        
        if (result.status !== 'success') {
          log.error('[notes_open] Failed to open window:', result.message || 'Unknown error');
        }
      }
      
      log.info('[notes_open] Note opened:', { id: note.id });
      
      return createSuccessResult(
        `Opened note: ${note.name}`,
        {
          noteId: note.id,
          title: note.name
        }
      );
    } catch (error) {
      log.error('[notes_open] Failed to open note:', error);
      return createErrorResult(
        'Failed to open note',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  }
};

export const notesExportCommand: SelfContainedCommand = {
  id: 'notes_export_md',
  description: '💾 EXPORT: Export note to markdown file',
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'noteId',
        type: 'string',
        description: 'ID of the note to export',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { noteId: 'note_abc123' },
        expectedOutput: {
          status: 'success',
          message: 'Exported note: My Note',
          format: 'json',
          data: { noteId: 'note_abc123', title: 'My Note', markdown: '# My Note\n\nContent here' }
        },
        description: 'Export a note to markdown'
      }
    ],
    bad: []
  },
  
  handler: async (params: { noteId: string }, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      log.info('[notes_export_md] Exporting note...', { noteId: params.noteId });
      
      const note = await entityService.get(params.noteId);
      if (!note || note.entityType !== 'Note') {
        return createErrorResult(
          'Note not found',
          'The requested note does not exist',
          'validation'
        );
      }
      
      const markdown = note.skeleton?.markdownExport || '';
      
      return createSuccessResult(
        `Exported note: ${note.name}`,
        {
          noteId: note.id,
          title: note.name,
          markdown
        }
      );
    } catch (error) {
      log.error('[notes_export_md] Failed to export note:', error);
      return createErrorResult(
        'Failed to export note',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  }
};

// V1 Commands (preferred - more features, better error handling, folder support)
import { notesCreateCommand as notesCreateCommandV1 } from './v1/notes_create';

export const notesCommands = [
  notesCreateCommandV1, // ✅ Use v1 - has folderId support for entity creation registry
  notesOpenCommand,
  notesExportCommand
];
